/**
 * Organization Metadata Field Definitions
 * Sourced from NewOrgFieldsV2.csv
 * Drives dynamic UI rendering — no per-field JSX needed.
 */

export type MetadataFieldType =
  | "dropdown"
  | "multi_select"
  | "checkbox"
  | "date"
  | "currency"
  | "text"
  | "integer"
  | "hyperlink";

export type MetadataCategoryId =
  | "status_inactivity"
  | "facility_use"
  | "llp_tracking"
  | "program_products"
  | "paperwork";

export interface MetadataFieldDef {
  key: string;
  label: string;
  category: MetadataCategoryId;
  type: MetadataFieldType;
  options?: string[];
  defaultValue?: string | boolean | number | null;
  /** Rendered on the same row, right after this field key (e.g. a "Last Verified" date beside what it verifies). */
  companionOf?: string;
  /** One-line help shown under the label. */
  hint?: string;
}

export const METADATA_CATEGORIES: {
  id: MetadataCategoryId;
  label: string;
}[] = [
  { id: "status_inactivity", label: "Status / Inactivity" },
  { id: "facility_use", label: "Facility Use Attributes" },
  { id: "llp_tracking", label: "LLP Tracking Data" },
  { id: "program_products", label: "Program Products" },
  { id: "paperwork", label: "Paperwork / Onboarding Checklist" },
];

/** Requirements §6.6 Inactive Reason picklist (BD default: as supplied). */
export const INACTIVE_REASON_OPTIONS = [
  "Customer List",
  "Social Media Marketing",
  "Advertising",
  "Average Monthly Spend",
  "Previous Facility Decline",
  "Competition",
  "No Longer in Business",
  "Unresponsive",
  "Other",
];

export const METADATA_FIELDS: MetadataFieldDef[] = [
  // ==============================
  // Status / Inactivity (requirements p.3; BD-5/12/13/18 defaults)
  // ==============================
  {
    key: "inactive",
    label: "Inactive",
    category: "status_inactivity",
    type: "checkbox",
    defaultValue: false,
    hint: "A record-keeping flag. The Active/Inactive status in the page header is what actually deactivates the organization.",
  },
  {
    key: "inactive_request",
    label: "Inactive (Request)",
    category: "status_inactivity",
    type: "checkbox",
    defaultValue: false,
  },
  {
    key: "inactive_request_note",
    label: "Request Note",
    category: "status_inactivity",
    type: "text",
    companionOf: "inactive_request",
  },
  {
    key: "inactive_request_date",
    label: "Requested On",
    category: "status_inactivity",
    type: "date",
    companionOf: "inactive_request",
  },
  {
    key: "inactive_reason_code",
    label: "Inactive (Reason)",
    category: "status_inactivity",
    type: "dropdown",
    options: INACTIVE_REASON_OPTIONS,
    defaultValue: null,
  },
  {
    key: "inactive_reason",
    label: "Reason Notes",
    category: "status_inactivity",
    type: "text",
    companionOf: "inactive_reason_code",
    hint: "Free-text reasons recorded before the picklist are kept here.",
  },
  {
    key: "inactive_date",
    label: "Inactive Date",
    category: "status_inactivity",
    type: "date",
  },
  {
    key: "competition",
    label: "Competition",
    category: "status_inactivity",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "competitor_name",
    label: "Competitor",
    category: "status_inactivity",
    type: "text",
    companionOf: "competition",
  },
  {
    key: "verification_date",
    label: "Verification Date",
    category: "status_inactivity",
    type: "date",
    hint: "Date the account details were last verified.",
  },

  // ==============================
  // Facility Use Attributes
  // ==============================
  {
    key: "storefront",
    label: "Storefront",
    category: "facility_use",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "classes_attended",
    label: "Classes Attended",
    category: "facility_use",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "classes_offered",
    label: "Classes Offered",
    category: "facility_use",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "member_benefits",
    label: "Member Benefits",
    category: "facility_use",
    type: "checkbox",
    defaultValue: false,
  },
  {
    key: "affiliate_link_on_website",
    label: "Affiliate Link on Website?",
    category: "facility_use",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "affiliate_link_date_last_verified",
    label: "Last Verified",
    category: "facility_use",
    type: "date",
    companionOf: "affiliate_link_on_website",
  },
  {
    key: "marketing_video_created",
    label: "Marketing Video Created?",
    category: "facility_use",
    type: "checkbox",
    defaultValue: false,
  },
  {
    key: "e_learning_implemented",
    label: "E-learning Implemented",
    category: "facility_use",
    type: "checkbox",
    defaultValue: false,
  },
  {
    key: "av_use",
    label: "AV Use",
    category: "facility_use",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "sales_tactic",
    label: "Sales Tactic",
    category: "facility_use",
    type: "dropdown",
    options: ["None", "Digital Only", "Sales Rep", "Sales Rep & Digital"],
    defaultValue: "None",
  },
  {
    key: "partner_classification",
    label: "Partner Classification",
    category: "facility_use",
    type: "dropdown",
    options: ["A", "B", "C", "D"],
    defaultValue: null,
  },
  {
    key: "gunsmith_on_staff",
    label: "Gunsmith on Staff",
    category: "facility_use",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "social_media_marketing",
    label: "Social Media Marketing",
    category: "facility_use",
    type: "multi_select",
    options: [
      "Blog",
      "YouTube",
      "Facebook",
      "Instagram",
      "Newsletter",
      "None",
      "Not Applicable",
      "X",
    ],
    defaultValue: null,
  },
  {
    key: "advertising",
    label: "Advertising",
    category: "facility_use",
    type: "multi_select",
    options: [
      "Television",
      "Social Media",
      "In-house Newsletter",
      "Email",
      "Billboards",
    ],
    defaultValue: null,
  },
  {
    key: "average_monthly_spend",
    label: "Average Monthly Spend",
    category: "facility_use",
    type: "currency",
  },
  {
    key: "w9",
    label: "W9",
    category: "facility_use",
    type: "dropdown",
    options: ["None", "Yes", "No"],
  },
  {
    key: "voucher_retailer_program",
    label: "Voucher Retailer Program",
    category: "facility_use",
    type: "checkbox",
    defaultValue: false,
  },
  {
    key: "effective_date_voucher_retailer",
    label: "Effective Date (Voucher Retailer)",
    category: "facility_use",
    type: "date",
  },
  {
    key: "voucher_gun_show_program",
    label: "Voucher Gun Show Program",
    category: "facility_use",
    type: "checkbox",
    defaultValue: false,
  },
  {
    key: "effective_date_voucher_gun_show",
    label: "Effective Date (Voucher Gun Show)",
    category: "facility_use",
    type: "date",
  },
  {
    key: "marketing_content",
    label: "Marketing Content",
    category: "facility_use",
    type: "multi_select",
    options: [
      "Videos",
      "Articles",
      "Podcasts",
      "eBooks",
      "Testimonials",
      "Images",
    ],
    defaultValue: null,
  },
  {
    key: "newsletter_prospect",
    label: "Newsletter Prospect",
    category: "facility_use",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "counter_sales_material_last_shipment",
    label: "Counter Sales Material - Date of Last Shipment",
    category: "facility_use",
    type: "date",
  },
  {
    key: "marketing_video_shared_on",
    label: "Marketing Video Shared On",
    category: "facility_use",
    type: "date",
  },
  {
    key: "onsite_partner_review_last_completed",
    label: "On-site Partner Review - Last Completed Date",
    category: "facility_use",
    type: "date",
  },
  {
    key: "shooting_qualification",
    label: "Shooting Qualification",
    category: "facility_use",
    type: "checkbox",
    defaultValue: false,
  },
  {
    key: "ltc_course_signup_url",
    label: "LTC Course Sign Up URL",
    category: "facility_use",
    type: "text",
  },
  {
    key: "co_renewal_course",
    label: "CO Renewal Course",
    category: "facility_use",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "ccic_instructor_qualifier",
    label: "CCIC Instructor Qualifier",
    category: "facility_use",
    type: "checkbox",
    defaultValue: false,
  },

  // ==============================
  // LLP Tracking Data (4)
  // ==============================
  {
    key: "type_of_retailer_agreement",
    label: "Type of Retailer Agreement",
    category: "llp_tracking",
    type: "dropdown",
    options: ["Flat Fee", "New PPM", "Outdated PPM", "Frozen"],
    defaultValue: null,
  },
  {
    key: "payment_amount",
    label: "Payment Amount",
    category: "llp_tracking",
    type: "currency",
  },
  {
    key: "date_of_last_agreement_review",
    label: "Date of Last Agreement Review",
    category: "llp_tracking",
    type: "date",
  },
  {
    key: "result_of_agreement_review",
    label: "Result of Agreement Review",
    category: "llp_tracking",
    type: "text",
  },

  // ==============================
  // Program Products (16)
  // ==============================
  {
    key: "direct_sales_activities",
    label: "Direct Sales Activities",
    category: "program_products",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "counter_cards_affiliate_links",
    label: "Counter Cards/Affiliate Links",
    category: "program_products",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "pos_system_lead_gen",
    label: "POS System Lead Gen",
    category: "program_products",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "online_ltc_learning_courses",
    label: "Online LTC/Learning Courses",
    category: "program_products",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "total_compensation",
    label: "Total Compensation",
    category: "program_products",
    type: "currency",
  },
  {
    key: "paid_per_member_amount",
    label: "Paid Per Member Amount",
    category: "program_products",
    type: "currency",
  },
  {
    key: "fma_amount",
    label: "FMA Amount",
    category: "program_products",
    type: "currency",
  },
  {
    key: "fma_conversation",
    label: "FMA Conversation",
    category: "program_products",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "fma_conversation_date",
    label: "FMA Conversation Date",
    category: "program_products",
    type: "date",
  },
  {
    key: "part_time_sales_rep",
    label: "Part-Time Sales Rep",
    category: "program_products",
    type: "text",
  },
  {
    key: "ffl_dealershield",
    label: "FFL DealerShield",
    category: "program_products",
    type: "checkbox",
    defaultValue: false,
  },
  {
    key: "instructor_program",
    label: "Instructor Program",
    category: "program_products",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "instructor_program_count",
    label: "Instructor Program Count",
    category: "program_products",
    type: "integer",
  },
  {
    key: "instructor_retailer_insurance",
    label: "Instructor Retailer Insurance",
    category: "program_products",
    type: "text",
  },
  {
    key: "newsletter_program",
    label: "Newsletter",
    category: "program_products",
    type: "dropdown",
    options: ["None", "Yes", "No"],
    defaultValue: "None",
  },
  {
    key: "recent_review_form",
    label: "Recent Review Form",
    category: "program_products",
    type: "hyperlink",
  },

  // ==============================
  // Paperwork (8 items, each with completed date + evidence link; BD-18 default)
  // ==============================
  {
    key: "paperwork_direct_sales",
    label: "Direct Sales (Classes)",
    category: "paperwork",
    type: "dropdown",
    options: ["Null", "Paperwork Completed", "Paperwork Pending"],
    defaultValue: null,
  },
  {
    key: "paperwork_direct_sales_completed_date",
    label: "Completed On",
    category: "paperwork",
    type: "date",
    companionOf: "paperwork_direct_sales",
  },
  {
    key: "paperwork_direct_sales_evidence_url",
    label: "Evidence Link",
    category: "paperwork",
    type: "hyperlink",
    companionOf: "paperwork_direct_sales",
  },
  {
    key: "paperwork_indirect_sales",
    label: "Indirect Sales (Affiliate Links)",
    category: "paperwork",
    type: "dropdown",
    options: ["Null", "Paperwork Completed", "Paperwork Pending"],
    defaultValue: null,
  },
  {
    key: "paperwork_indirect_sales_completed_date",
    label: "Completed On",
    category: "paperwork",
    type: "date",
    companionOf: "paperwork_indirect_sales",
  },
  {
    key: "paperwork_indirect_sales_evidence_url",
    label: "Evidence Link",
    category: "paperwork",
    type: "hyperlink",
    companionOf: "paperwork_indirect_sales",
  },
  {
    key: "paperwork_online_ltc",
    label: "Online LTC",
    category: "paperwork",
    type: "dropdown",
    options: ["Null", "Paperwork Completed", "Paperwork Pending"],
    defaultValue: null,
  },
  {
    key: "paperwork_online_ltc_completed_date",
    label: "Completed On",
    category: "paperwork",
    type: "date",
    companionOf: "paperwork_online_ltc",
  },
  {
    key: "paperwork_online_ltc_evidence_url",
    label: "Evidence Link",
    category: "paperwork",
    type: "hyperlink",
    companionOf: "paperwork_online_ltc",
  },
  {
    key: "paperwork_point_of_sale",
    label: "Point of Sale",
    category: "paperwork",
    type: "dropdown",
    options: ["Null", "Paperwork Completed", "Paperwork Pending"],
    defaultValue: null,
  },
  {
    key: "paperwork_point_of_sale_completed_date",
    label: "Completed On",
    category: "paperwork",
    type: "date",
    companionOf: "paperwork_point_of_sale",
  },
  {
    key: "paperwork_point_of_sale_evidence_url",
    label: "Evidence Link",
    category: "paperwork",
    type: "hyperlink",
    companionOf: "paperwork_point_of_sale",
  },
  {
    key: "paperwork_dealershield",
    label: "DealerShield",
    category: "paperwork",
    type: "dropdown",
    options: ["Null", "Paperwork Completed", "Paperwork Pending"],
    defaultValue: null,
  },
  {
    key: "paperwork_dealershield_completed_date",
    label: "Completed On",
    category: "paperwork",
    type: "date",
    companionOf: "paperwork_dealershield",
  },
  {
    key: "paperwork_dealershield_evidence_url",
    label: "Evidence Link",
    category: "paperwork",
    type: "hyperlink",
    companionOf: "paperwork_dealershield",
  },
  {
    key: "paperwork_instructor_certification",
    label: "Instructor Certification",
    category: "paperwork",
    type: "dropdown",
    options: ["Null", "Paperwork Completed", "Paperwork Pending"],
    defaultValue: null,
  },
  {
    key: "paperwork_instructor_certification_completed_date",
    label: "Completed On",
    category: "paperwork",
    type: "date",
    companionOf: "paperwork_instructor_certification",
  },
  {
    key: "paperwork_instructor_certification_evidence_url",
    label: "Evidence Link",
    category: "paperwork",
    type: "hyperlink",
    companionOf: "paperwork_instructor_certification",
  },
  {
    key: "paperwork_insurance",
    label: "Insurance",
    category: "paperwork",
    type: "dropdown",
    options: ["Null", "Paperwork Completed", "Paperwork Pending"],
    defaultValue: null,
  },
  {
    key: "paperwork_insurance_completed_date",
    label: "Completed On",
    category: "paperwork",
    type: "date",
    companionOf: "paperwork_insurance",
  },
  {
    key: "paperwork_insurance_evidence_url",
    label: "Evidence Link",
    category: "paperwork",
    type: "hyperlink",
    companionOf: "paperwork_insurance",
  },
  {
    key: "paperwork_newsletter",
    label: "Newsletter",
    category: "paperwork",
    type: "dropdown",
    options: ["Null", "Paperwork Completed", "Paperwork Pending"],
    defaultValue: null,
  },
  {
    key: "paperwork_newsletter_completed_date",
    label: "Completed On",
    category: "paperwork",
    type: "date",
    companionOf: "paperwork_newsletter",
  },
  {
    key: "paperwork_newsletter_evidence_url",
    label: "Evidence Link",
    category: "paperwork",
    type: "hyperlink",
    companionOf: "paperwork_newsletter",
  },
];

/** Get fields filtered by category */
export const getFieldsByCategory = (
  category: MetadataCategoryId,
): MetadataFieldDef[] => METADATA_FIELDS.filter((f) => f.category === category);

/** Fields that render as their own cell (companions render beside their parent). */
export const getPrimaryFieldsByCategory = (
  category: MetadataCategoryId,
): MetadataFieldDef[] =>
  getFieldsByCategory(category).filter((f) => !f.companionOf);

/** Companion fields rendered on the same row as `parentKey`. */
export const getCompanionFields = (parentKey: string): MetadataFieldDef[] =>
  METADATA_FIELDS.filter((f) => f.companionOf === parentKey);
